import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Virginia Rehab Physicians';
const SITE_URL = 'https://varehabphysicians.com';
const DEFAULT_DESCRIPTION =
  'Virginia Rehab Physicians is a physician-owned PM&R group that medically directs and manages inpatient rehabilitation units, with the workflows, software, and support that make great rehab care repeatable.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * Per-page <head> management: title, meta description, canonical, Open Graph,
 * Twitter, and optional JSON-LD structured data.
 *
 * Usage:
 *   <Seo
 *     title="For Hospitals & Facilities"
 *     description="..."
 *     path="/for-facilities"
 *     jsonLd={[ ...schemaObjects ]}
 *   />
 *
 * `title` is suffixed with the site name automatically (pass titleOverride to
 * provide the full <title> verbatim, e.g. for the homepage).
 */
function Seo({
  title,
  titleOverride,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd,
}) {
  const fullTitle =
    titleOverride || (title ? `${title} | ${SITE_NAME}` : `${SITE_NAME}: Physician-Owned Inpatient Rehabilitation`);
  const canonical = `${SITE_URL}${path === '/' ? '' : path}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((schema, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export const SEO_SITE_URL = SITE_URL;
export const SEO_SITE_NAME = SITE_NAME;
export default Seo;
